import { useEffect, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { api } from '../../lib/api.js';
import { useApi } from '../../hooks/useApi.js';
import { useAuth } from '../../hooks/useAuth.jsx';
import { Button } from '../../components/Button.jsx';
import {
  EVENT_STATUS_LABEL, EVENT_STATUS_PILL, KIND_PILL,
  MAGENTA, formatDateTime,
} from './maintenance-shared.js';
import { NewEventModal } from './NewEventModal.jsx';
import { TicketNumberEditor } from './TicketNumberEditor.jsx';

// Equipment has no detail route (server-only module) — only link when this
// is a printer; render equipment (or a deleted machine) as plain text.
const machineHref = (e) => (e.printerId ? `/printers/${e.printerId}` : null);

const ConsumptionEditor = ({ event, onChanged }) => {
  const { data: items } = useApi('/inventory?status=active');
  const [rows, setRows] = useState(event.consumptions ?? []);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setRows(event.consumptions ?? []); }, [event.consumptions]);

  const addRow = () => setRows((r) => [...r, { inventoryItemId: null, quantity: 1, itemName: '' }]);
  const removeRow = (i) => setRows((r) => r.filter((_, idx) => idx !== i));
  const setItem = (i, itemId) => setRows((r) => r.map((row, idx) => idx === i
    ? { ...row, inventoryItemId: Number(itemId), itemName: items.find((x) => x.id === Number(itemId))?.name }
    : row));
  const setQty = (i, qty) => setRows((r) => r.map((row, idx) => idx === i ? { ...row, quantity: Math.max(1, Number(qty) || 1) } : row));

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/maintenance/events/${event.id}`, {
        consumptions: rows.filter((r) => r.inventoryItemId).map((r) => ({
          inventoryItemId: r.inventoryItemId,
          quantity: r.quantity,
        })),
      });
      onChanged();
    } catch (err) {
      alert(err.message ?? 'Failed to save');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="overflow-hidden rounded-lg border border-border-subtle bg-bg-card">
      {rows.length === 0 && (
        <p class="m-0 px-4 py-3 text-[13px] text-text-tertiary">No consumption recorded.</p>
      )}
      {rows.map((row, i) => (
        <div key={i} class="grid grid-cols-[1fr_80px_auto] items-center gap-3 border-b border-border-subtle px-4 py-2 last:border-b-0">
          <select
            value={row.inventoryItemId ?? ''}
            onChange={(e) => setItem(i, e.currentTarget.value)}
            class="rounded-md border border-border-strong bg-bg-base px-2 py-1 text-sm font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
          >
            <option value="">Pick an item…</option>
            {(items ?? []).map((it) => <option key={it.id} value={it.id}>{it.name}{it.productId ? ` · ${it.productId}` : ''}</option>)}
          </select>
          <input
            type="number" min={1} value={row.quantity}
            onInput={(e) => setQty(i, e.currentTarget.value)}
            class="rounded-md border border-border-strong bg-bg-base px-2 py-1 text-right font-serif text-base text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
          />
          <button type="button" onClick={() => removeRow(i)} class="text-text-muted hover:text-ink-magenta">✕</button>
        </div>
      ))}
      <div class="flex items-center justify-between gap-2 border-t border-border-subtle bg-bg-header px-4 py-2">
        <Button variant="secondary" onClick={addRow}>+ Add item</Button>
        <Button variant="primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save consumption'}</Button>
      </div>
      <p class="m-0 px-4 py-2 text-[11px] text-text-muted">
        Saving decrements stock when the event is "completed". Editing an already-completed event reverses previous decrements first.
      </p>
    </div>
  );
};

export const EventDetailPage = ({ id }) => {
  const { route } = useLocation();
  const { data: event, loading, error, refetch } = useApi(`/maintenance/events/${id}`);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [busy, setBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const patchStatus = async (status) => {
    setBusy(true);
    try {
      await api.patch(`/maintenance/events/${id}`, {
        status,
        performedAt: status === 'completed' ? new Date().toISOString().slice(0, 10) : event.performedAt,
      });
      refetch();
    } catch (err) {
      alert(err.message ?? 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('Delete this event? Stock consumed will be returned to inventory.')) return;
    try {
      await api.delete(`/maintenance/events/${id}`);
      route('/maintenance?tab=log');
    } catch (err) {
      alert(err.message ?? 'Failed');
    }
  };

  if (loading) return <p class="text-sm text-text-tertiary">Loading…</p>;
  if (error) return <p class="text-sm font-semibold text-ink-magenta">Failed to load: {error.message}</p>;
  if (!event) return null;

  return (
    <div>
      <a href="/maintenance?tab=log" class="mb-3 inline-block text-[12px] font-semibold uppercase tracking-eyebrow text-text-tertiary hover:text-text-primary">
        ← Log
      </a>

      <section class="mb-6 flex items-start justify-between gap-4">
        <div class="min-w-0">
          <div class="mb-1.5 flex items-center gap-2">
            <span class="block h-2.5 w-2.5 rounded-full" style={{ background: MAGENTA }} />
            <span class="text-[11px] font-semibold uppercase tracking-eyebrow" style={{ color: MAGENTA }}>
              Event <TicketNumberEditor row={event} resource="events" isAdmin={isAdmin} onSaved={refetch} />
              {' · '}{event.kind === 'visit' ? 'External visit' : 'In-house routine'}
            </span>
          </div>
          <h1 class="m-0 mb-2 text-[28px] leading-[1.18] tracking-tight text-text-primary">{event.title}</h1>
          <div class="flex flex-wrap items-center gap-2">
            <span class={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${KIND_PILL[event.kind]}`}>
              {event.kind === 'routine' ? 'Routine' : 'Visit'}
            </span>
            <span class={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${EVENT_STATUS_PILL[event.status]}`}>
              {EVENT_STATUS_LABEL[event.status]}
            </span>
            {machineHref(event) ? (
              <a href={machineHref(event)}
                class="inline-flex items-center gap-1.5 rounded-full border border-border-strong bg-bg-base px-2.5 py-0.5 text-[11px] font-semibold text-text-tertiary hover:text-text-body">
                <span class="block h-1.5 w-1.5 rounded-full bg-ink-teal" />
                {event.machineName ?? '—'}
              </a>
            ) : (
              <span class="inline-flex items-center gap-1.5 rounded-full border border-border-strong bg-bg-base px-2.5 py-0.5 text-[11px] font-semibold text-text-tertiary">
                <span class="block h-1.5 w-1.5 rounded-full bg-ink-teal" />
                {event.machineName ?? '—'}
              </span>
            )}
          </div>
        </div>
        <div class="flex shrink-0 gap-2">
          {event.status === 'scheduled' && (
            <Button variant="primary" onClick={() => patchStatus('completed')} disabled={busy}>Mark completed</Button>
          )}
          {event.status === 'scheduled' && (
            <Button variant="secondary" onClick={() => patchStatus('missed')} disabled={busy}>Mark missed</Button>
          )}
          <Button variant="secondary" onClick={() => setEditOpen(true)}>Edit</Button>
          {isAdmin && (
            <Button variant="ghost" onClick={remove} class="text-ink-magenta hover:text-ink-magenta">Delete</Button>
          )}
        </div>
      </section>

      <NewEventModal
        open={editOpen}
        event={event}
        onClose={() => setEditOpen(false)}
        onSaved={() => { setEditOpen(false); refetch(); }}
      />

      <div class="mb-6 grid grid-cols-1 gap-3 md:grid-cols-2">
        <div class="rounded-lg border border-border-subtle bg-bg-card p-4">
          <p class="m-0 mb-2 text-[11px] font-semibold uppercase tracking-eyebrow text-text-tertiary">When</p>
          <table class="w-full text-[13px]">
            <tbody>
              <tr><td class="w-2/5 py-1 text-text-tertiary">Scheduled</td><td class="py-1 text-text-body">{formatDateTime(event.scheduledAt)}</td></tr>
              <tr><td class="py-1 text-text-tertiary">Performed</td><td class="py-1 text-text-body">{formatDateTime(event.performedAt)}</td></tr>
              <tr><td class="py-1 text-text-tertiary">Logged by</td><td class="py-1 text-text-body">{event.performedByName ?? <span class="text-text-muted">—</span>}</td></tr>
            </tbody>
          </table>
        </div>
        <div class="rounded-lg border border-border-subtle bg-bg-card p-4">
          <p class="m-0 mb-2 text-[11px] font-semibold uppercase tracking-eyebrow text-text-tertiary">Technician</p>
          <p class="m-0 text-[13px] text-text-body">{event.technicianName ?? <span class="text-text-muted">In-house</span>}</p>
        </div>
      </div>

      {event.notes && (
        <div class="mb-6 rounded-lg border border-border-subtle bg-bg-card p-4">
          <p class="m-0 mb-2 text-[11px] font-semibold uppercase tracking-eyebrow text-text-tertiary">Notes</p>
          <p class="m-0 whitespace-pre-wrap text-[13px] font-medium leading-relaxed text-text-body">{event.notes}</p>
        </div>
      )}

      <h2 class="mb-3 font-serif text-[18px] font-medium text-text-primary">Inventory consumed</h2>
      <div class="mb-6">
        <ConsumptionEditor event={event} onChanged={refetch} />
      </div>

      {event.relatedIssueTitle && (
        <div class="rounded-lg border border-border-subtle bg-bg-card p-4">
          <p class="m-0 text-[11px] font-semibold uppercase tracking-eyebrow text-text-tertiary">Related issue</p>
          <a href={`/maintenance/issues/${event.relatedIssueId}`} class="m-0 mt-1 block text-[13px] font-semibold text-brand hover:underline">
            {event.relatedIssueTitle}
          </a>
        </div>
      )}
    </div>
  );
};
